import { Injectable } from '@angular/core';

import * as _ from 'lodash';

import { environment } from 'src/environments/environment';

import { StorageApiSearch } from 'src/app/api/services/api-utils.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { ListItem, ListItemKind } from 'src/app/components/scrolling-list';

export type StorageSelectorType =
  | 'single-file'
  | 'single-folder'
  | 'multiple-file'
  | 'multiple-files-folders';

export interface StorageSelectorFilter {
  name: '' | 'images' | 'videos' | 'document' | 'folders' | 'fonts' | 'script';
  extensions: string[] | null;
}

@Injectable({
  providedIn: 'root',
})
export class StorageUtilsService {
  static readonly STORAGE_FILE_URL = 'https://storage.googleapis.com/';
  static readonly STORAGE_CLIENT_API =
    'https://www.googleapis.com/storage/v1/b/';
  static readonly SELECTOR_TYPES: Record<string, StorageSelectorType> = {
    SINGLE_FILE: 'single-file',
    MULTIPLE_FILE: 'multiple-file',
    SINGLE_FOLDER: 'single-folder',
    MULTIPLE_FILES_FOLDERS: 'multiple-files-folders',
  };
  static readonly SELECTOR_FILTERS: Record<string, StorageSelectorFilter> = {
    ALL: {
      name: '',
      extensions: null,
    },
    IMAGES: {
      name: 'images',
      extensions: ['jpg', 'jpeg', 'png', 'bmp', 'svg', 'gif', 'webp'],
    },
    VIDEOS: {
      name: 'videos',
      extensions: ['webm', 'mp4', 'ogg', 'ogv'],
    },
    DOCUMENT: {
      name: 'document',
      extensions: ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'],
    },
    FOLDERS: {
      name: 'folders',
      extensions: ['/'],
    },
    FONTS: {
      name: 'fonts',
      extensions: ['ttf', 'otf', 'woff', 'woff2'],
    },
    SCRIPTS: {
      name: 'script',
      extensions: ['js', 'html', 'css'],
    },
  };

  static readonly STORAGE_CLIENT_ERROR = {
    'blocked-file':
      'This file is being requested too many times and is blocked.',
    'duplicate-folder':
      'A Folder already uses that name, please choose a different one',
    move: {
      'both-files-or-folders': 'Destination must be a folder.',
      'both-root-or-trash':
        'Source and destination must be both in Root or in Trash.',
      'destination-must-not-exist':
        'Destination folder already contains a file with that name.',
      'invalid-name': 'The new name cannot have slashes.',
      'must-be-different':
        'You cannot move these files or folders into the same folder. Please select a different folder and try again.',
      'no-matches-found': 'No matching files found.',
      'parent-must-exist': 'Destination parent directory must exist.',
      'protected-object-id': 'This file is protected and cannot be moved.',
      'root-not-valid':
        'Source and destination cannot point to the root folder.',
      'subfolders-not-valid':
        'Source and destination cannot be subfolders of each other.',
      unknown: 'An error occured while moving. Please try again.',
    },
    'server-error': 'An error has occured.',
    'signed-download-uri-request-failed': 'Download failed. Please try again.',
    'upload-failed': 'Upload failed. Please try again.',
    'upload-file-not-supported':
      'File type not supported. File did not upload.',
    rename: {
      'both-files-or-folders':
        'Source and destination must be both files or folders.',
      'both-root-or-trash':
        'Source and destination must be both in Root or in Trash.',
      'destination-must-not-exist': 'Name is in use, please choose another.',
      'invalid-name': 'The new name cannot have slashes.',
      'must-be-different': 'The new name must be different.',
      'no-matches-found': 'No matching files found.',
      'parent-must-exist': 'Destination parent directory must exist.',
      'potentially-dangerous-file':
        'This does not appear to be a supported media (video, image) file.',
      'protected-object-id': 'This file is protected and cannot be renamed.',
      'root-not-valid':
        'Source and destination cannot point to the root folder.',
      'subfolders-not-valid':
        'Source and destination cannot be subfolders of each other.',
      unknown: 'An error occured while renaming. Please try again.',
    },
  };
  static readonly STORAGE_CLIENT_TITLE = {
    'select-file': 'Select a File',
    'select-folder-to-update':
      'Select a Folder to show and update Images and Videos as you add or remove them from the Folder.',
    'select-multiple-file': 'Select one or more Files',
    'select-multiple-files-folders': 'Select Files and Folders',
    'select-multiple-files-folders-images':
      'Select Images and/or Folders of Images',
    'select-multiple-files-folders-videos':
      'Select Videos and/or Folders of Videos',
    'select-single-file': 'Select a File',
    'select-single-file-images': 'Select an Image',
    'select-single-file-videos': 'Select a Video',
    'select-single-folder': 'Select a Folder',
    'select-single-folder-images': 'Select a Folder of Images',
    'select-single-folder-videos': 'Select a Folder of Videos',
  };
  static readonly TRASH_PLACEHOLDER = '--TRASH--';
  static readonly TRASH_FOLDER = '--TRASH--/';
  static readonly TRASH_LABEL = 'Trash';

  constructor(private companyStateService: CompanyStateService) {}

  fileIsFolder(file: StorageFile): boolean {
    return file.name.endsWith('/') || file.name === '';
  }

  fileIsTrash(file: StorageFile): boolean {
    return file.name === StorageUtilsService.TRASH_FOLDER;
  }

  isTrashFolder(path: string): boolean {
    return path?.lastIndexOf(StorageUtilsService.TRASH_FOLDER, 0) === 0;
  }

  fileName(file: StorageFile): string {
    return file.name.substring(this.fileParent(file).length);
  }

  fileParent(file: StorageFile): string {
    const idx = file.name.length - (this.fileIsFolder(file) ? 2 : 1);

    return file.name.substring(0, file.name.lastIndexOf('/', idx) + 1);
  }

  getBucketName(): string {
    return (
      'risemedialibrary-' + this.companyStateService.getSelectedCompanyId()
    );
  }

  getFolderSelfLinkUrl(): string {
    return (
      StorageUtilsService.STORAGE_CLIENT_API +
      this.getBucketName() +
      '/o?prefix='
    );
  }

  isDefaultFileOnTestAppsEnvironment(fileName: string) {
    if (environment.production) {
      return false;
    }

    // all default files for Rise Vision templates are defined under this GCS bucket
    const regex =
      /^risemedialibrary-7fa5ee92-7deb-450b-a8d5-e5ed648c575f[/]Template Library[/].+/;

    return regex.test(fileName);
  }

  parseFilePath(fileName: string): string[] | null {
    const regex = /risemedialibrary-([0-9a-f-]{36})[/](.+)/g;
    const match = regex.exec(fileName);

    if (!match) {
      console.error('Filename is not a valid Rise Storage path: ' + fileName);

      return null;
    }

    return match;
  }

  getFileType(filename: string): string {
    if (filename === StorageUtilsService.TRASH_FOLDER) {
      return 'Trash folder';
    }
    if (filename.endsWith('/')) {
      return 'Folder';
    }

    const re = /(?:\.([^.]+))?$/;
    const ext = re.exec(filename)[1];

    if (ext && ext.length <= 5) {
      return ext.toUpperCase();
    }

    return '';
  }

  fileHasExtension(file: StorageFile, allowedExtensions: string[]) {
    const fileType = this.getFileType(file.name);
    return allowedExtensions.indexOf(fileType.toLowerCase()) !== -1;
  }

  fileIsImage(file: StorageFile) {
    return this.fileHasExtension(
      file,
      StorageUtilsService.SELECTOR_FILTERS.IMAGES.extensions,
    );
  }

  fileIsVideo(file: StorageFile) {
    return this.fileHasExtension(
      file,
      StorageUtilsService.SELECTOR_FILTERS.VIDEOS.extensions,
    );
  }

  fileIsDocument(file: StorageFile) {
    return this.fileHasExtension(
      file,
      StorageUtilsService.SELECTOR_FILTERS.DOCUMENT.extensions,
    );
  }

  fileIsFont(file: StorageFile) {
    return this.fileHasExtension(
      file,
      StorageUtilsService.SELECTOR_FILTERS.FONTS.extensions,
    );
  }

  fileIsScript(file: StorageFile) {
    return this.fileHasExtension(
      file,
      StorageUtilsService.SELECTOR_FILTERS.SCRIPTS.extensions,
    );
  }

  getFilePathWithTimeCreated(file: StorageFile) {
    return this.getFilePath(file) + this.getTimeCreatedQuery(file);
  }

  getFilePath(file: StorageFile) {
    if (!file.bucket) {
      file.bucket = this.getBucketName();
    }

    return file.bucket + '/' + file.name;
  }

  storageFileUrlFor(fileName: string): string {
    return StorageUtilsService.STORAGE_FILE_URL + encodeURI(fileName);
  }

  fileNameFromUrl(url: string): string {
    const withoutBase = url.startsWith(StorageUtilsService.STORAGE_FILE_URL)
      ? url.slice(StorageUtilsService.STORAGE_FILE_URL.length)
      : url;
    const firstSlash = withoutBase.indexOf('/');
    return firstSlash >= 0 ? withoutBase.slice(firstSlash + 1) : withoutBase;
  }

  getTimeCreatedQuery(file: StorageFile) {
    const timeCreated = this.timeCreatedFor(file);

    return timeCreated ? '?_=' + timeCreated : '';
  }

  timeCreatedFor(file: StorageFile) {
    return file.timeCreated && file.timeCreated.value;
  }

  isFolder(path: string) {
    return path[path.length - 1] === '/';
  }

  parseFolderName(path: string) {
    if (!path) return '';
    path = path.trim().replace(/\/+$/, ''); // Remove trailing slashes
    let slash = path.lastIndexOf('/');
    return slash < 0 ? path : path.substring(slash + 1);
  }

  parseStoragePath(path: string) {
    if (!path) return '';
    if (path.indexOf('risemedialibrary') === 0) {
      let start = path.indexOf('/');
      return start < 0 ? '/' : path.substring(start);
    }
    return path;
  }

  getUploadedFolder(name: string, rootFolder?: string) {
    if (!name) return '';
    if (name.indexOf(rootFolder) === -1) return name;

    // Get first slash after rootFolder path
    let firstSlash = name.indexOf('/', rootFolder.length);

    return name.substring(0, firstSlash + 1);
  }

  fileNameOf(path: string) {
    var parts = path.split('/');

    if (this.isFolder(path)) {
      return parts[parts.length - 2];
    } else {
      return parts.pop();
    }
  }

  fileNameWithoutExtension(path: string): string {
    return (this.fileNameOf(path) || '').replace(/\.[^/.]+$/, '');
  }

  getDuplicateRenameCandidate(filePath: string, copyNumber: number): string {
    const folderEnd = filePath.lastIndexOf('/') + 1;
    const folder = filePath.slice(0, folderEnd);
    const basename = filePath.slice(folderEnd);
    const dotIndex = basename.lastIndexOf('.');

    if (dotIndex <= 0) {
      return `${folder}${basename} (${copyNumber})`;
    }

    return (
      folder +
      `${basename.slice(0, dotIndex)} (${copyNumber})${basename.slice(dotIndex)}`
    );
  }

  getFreeRenameCandidate(
    filePath: string,
    takenNames: Set<string>,
    startAt = 1,
  ): { name: string; copyNumber: number } {
    let copyNumber = startAt;
    let name = this.getDuplicateRenameCandidate(filePath, copyNumber);

    while (takenNames.has(name)) {
      copyNumber++;
      name = this.getDuplicateRenameCandidate(filePath, copyNumber);
    }

    return { name, copyNumber };
  }

  hasRegularFileItems(folderItems: StorageFile[], includeFolders?: boolean) {
    var regularFiles = _.filter(folderItems, (item) => {
      return includeFolders || !this.isFolder(item.name);
    });

    return regularFiles.length > 0;
  }

  fileSortValue(file: StorageFile, field: string): string | number {
    if (file.name === StorageUtilsService.TRASH_FOLDER) {
      return StorageUtilsService.TRASH_FOLDER;
    }

    switch (field) {
      case 'name':
        return file.name.toLowerCase().split(' (').join('/(');
      case 'dateModified':
        return file.updated ? file.updated.value : '';
      case 'extension':
        if (file.name.endsWith('/')) {
          return 'Folder';
        }
        const ext = file.name.substring(file.name.lastIndexOf('.') + 1);
        return ext === file.name ? '' : ext;
      case 'size':
        return file.size ? Number(file.size) : 0;
      default:
        return '';
    }
  }

  private _sortFiles(
    filtered: ListItem<StorageFile>[],
    search: any,
  ): ListItem<StorageFile>[] {
    filtered.sort((a, b) => {
      const term1 = this.fileSortValue(a.item, search.sortBy);
      const term2 = this.fileSortValue(b.item, search.sortBy);
      if (term1 === StorageUtilsService.TRASH_FOLDER) {
        return -Infinity;
      }
      const order = term1 < term2 ? -1 : term1 > term2 ? 1 : 0;
      return search.reverse ? -order : order;
    });
    return filtered;
  }

  filterFiles(
    files: ListItem<StorageFile>[],
    search: StorageApiSearch,
    storageFull: boolean,
    ignoreFolders = false,
  ): ListItem<StorageFile>[] {
    const filtered = files.filter((file) => {
      return (
        (!ignoreFolders || !this.fileIsFolder(file.item)) &&
        (!search.query ||
          file.item.name.toLowerCase().indexOf(search.query.toLowerCase()) >=
            0) &&
        (storageFull || !this.fileIsTrash(file.item))
      );
    });
    return this._sortFiles(filtered, search);
  }

  filterFolders(
    files: ListItem<StorageFile>[],
    search: StorageApiSearch,
    storageFull: boolean,
  ): ListItem<StorageFile>[] {
    const filtered = files.filter((file) => {
      return (
        this.fileIsFolder(file.item) &&
        (!search.query ||
          file.item.name.toLowerCase().indexOf(search.query.toLowerCase()) >=
            0) &&
        (storageFull || !this.fileIsTrash(file.item))
      );
    });
    return this._sortFiles(filtered, search);
  }

  private _getFileUrl(file: StorageFile): string {
    const fileUrl =
      file.kind === 'folder'
        ? this.getFolderSelfLinkUrl() + encodeURIComponent(file.name)
        : StorageUtilsService.STORAGE_FILE_URL +
          this.getBucketName() +
          '/' +
          encodeURIComponent(file.name);

    return fileUrl;
  }

  getFileUrls(files: StorageFile[]): string[] {
    const fileUrls = [];

    if (files) {
      files.forEach((file) => {
        const copyUrl = this._getFileUrl(file);
        fileUrls.push(copyUrl);
      });
    }

    return fileUrls;
  }

  storageTitle(
    selectorType: StorageSelectorType,
    filterName: StorageSelectorFilter['name'],
  ): string {
    const key = 'select-' + selectorType + (filterName ? '-' + filterName : '');
    return StorageUtilsService.STORAGE_CLIENT_TITLE[key] || '';
  }
}

export interface StorageFile {
  name: string;
  kind?: ListItemKind;
  bucket?: string;
  contentType?: string;
  cacheControl?: string;
  crc32c?: string;
  etag?: string;
  generation?: number;
  id?: string;
  md5Hash?: string;
  mediaLink?: string;
  metadata?: {
    [key: string]: string;
  };
  metageneration?: number;
  selfLink?: string;
  size?: number;
  storageClass?: string;
  timeCreated?: {
    value: number;
    dateOnly: boolean;
    timeZoneShift: number;
  };
  timeStorageClassUpdated?: {
    value: number;
    dateOnly: boolean;
    timeZoneShift: number;
  };
  updated?: {
    value: number;
    dateOnly: boolean;
    timeZoneShift: number;
  };
  timeFinalized?: string;
  isThrottled?: boolean;
  showThrottledCallout?: boolean;
  folderId?: never;
  isFirstFile?: boolean;
}

export interface StorageFilesDetails {
  assetFolders?: any;
  bucketExists?: boolean;
  code?: number;
  message?: string;
  result?: boolean;
  serviceAssets?: any;
  userEmail?: string;
}
